import clsx from 'clsx';
import Button from 'components/atoms/Button/Button';
import Card from 'components/atoms/Card/Card';
import { Checkbox } from 'components/atoms/Checkbox/Checkbox';
import Heading from 'components/atoms/Heading/Heading';
import RadioButton from 'components/atoms/RadioButtons/RadioButton';
import RadioGroup from 'components/atoms/RadioButtons/RadioGroup';
import { RedemptionTypeKey } from 'components/molecules/RedemptionTypesButtons/__generated__/RedemptionTypesButtons_redemptionTypes.graphql';
import { Filters } from 'components/pages/RewardOptions/RewardOptions';
import { ChangeEvent, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { formatRange } from 'utils/formHelper';
import classes from './RedemptionFacets.module.scss';
import { RedemptionFacets_redemptionTypes$key } from './__generated__/RedemptionFacets_redemptionTypes.graphql';
import Txt from 'components/atoms/Txt/Txt';

export type Props = {
  className?: string;
  redemptionTypesRef: RedemptionFacets_redemptionTypes$key;
  initialType: RedemptionTypeKey;
  onFiltersChange: (filters: Partial<Filters>) => void;
  onFiltersApply: () => void;
};

export const REDEMPTIONS_PROVIDER_DATA: { [key in RedemptionTypeKey]: string } = {
  PAYPAL: 'PayPal',
  VENMO: 'Venmo',
  GIFT_CARD: 'E-Gift Card',
  CHARITY: 'Charity',
  MOBILE: 'Mobile Top-Up',
};

const fragmentSpec = graphql`
  fragment RedemptionFacets_redemptionTypes on RedemptionTypes
  @refetchable(queryName: "RedemptionFacetsRefetchQuery") {
    types {
      id
      name
    }
  }
`;

const pointsFilters: { [key: string]: string } = {};
pointsFilters[formatRange(0, 1000)] = '< 1000';
pointsFilters[formatRange(1000, 1999)] = '1000 - 2000';
pointsFilters[formatRange(2000, 2999)] = '2000 - 3000';
pointsFilters[formatRange(3000, 5000)] = '3000 - 5000';
pointsFilters[formatRange(5001, 99999)] = '> 5000';

export default function RedemptionFacets({
  className,
  redemptionTypesRef,
  initialType,
  onFiltersChange,
  onFiltersApply,
}: Props) {
  const data = useFragment<RedemptionFacets_redemptionTypes$key>(fragmentSpec, redemptionTypesRef);
  const [filters, setFilters] = useState<Partial<Filters>>({
    type: initialType,
    points: [],
  });

  function handleFilterChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.currentTarget.name === 'type') {
      const _filters = {
        ...filters,
        type: e.currentTarget.value as RedemptionTypeKey,
      };
      setFilters(_filters);
      onFiltersChange(_filters);
    } else if (e.currentTarget.name === 'points') {
      const points = filters.points || [];
      if (e.currentTarget.checked) {
        points.push(e.currentTarget.value);
      } else {
        const index = points.indexOf(e.currentTarget.value);
        if (index > -1) {
          points.splice(index, 1);
        }
      }
      const _filters = {
        ...filters,
        points,
      };
      setFilters(_filters);
      onFiltersChange(_filters);
    }
  }

  return (
    <Card className={clsx(classes.root, className)}>
      <Heading level={4} className={clsx(classes.headings)}>
        Reward Options
      </Heading>
      <RadioGroup className={clsx(classes.radioGroups)}>
        {data.types.map((type) => (
          <RadioButton
            value={type.name}
            key={type.id}
            label={REDEMPTIONS_PROVIDER_DATA[type.name]}
            name={'type'}
            onInput={handleFilterChange}
            defaultChecked={type.name === initialType}
          />
        ))}
      </RadioGroup>
      <Heading level={4} className={clsx(classes.headings)}>
        Filter by Lifepoints
      </Heading>
      <RadioGroup className={clsx(classes.radioGroups)}>
        {Object.entries(pointsFilters).map(([value, label]) => (
          <Checkbox
            value={value}
            key={value}
            skipTranslation
            label={label}
            name={'points'}
            onInput={handleFilterChange}
          />
        ))}
      </RadioGroup>
      <Button
        className={clsx(classes.applyButton)}
        buttonType={'primary'}
        buttonSize={'large'}
        onClick={onFiltersApply}
      >
        <Txt>Apply filters</Txt>
      </Button>
    </Card>
  );
}
